import { create } from 'zustand'
import { getAuthInstance } from '@/lib/firebase'
import { initSync, stopSync, clearSyncQueue } from '@/services/firestoreSync'
import { clearAllData } from '@/services/database'
import type { User } from 'firebase/auth'

export type SyncStatus = 'idle' | 'syncing' | 'synced' | 'error'

interface AuthUser {
  uid: string
  email: string
  displayName: string
  photoURL: string
}

interface AuthState {
  user: AuthUser | null
  memoUid: string | null
  memoConnected: boolean
  isLoading: boolean
  isSigningIn: boolean
  syncStatus: SyncStatus
  lastSyncTime: string | null
  lastSyncError: string | null
  error: string | null

  initialize: () => void
  login: () => Promise<void>
  logout: () => Promise<void>
  retrySync: () => Promise<void>
  setSyncStatus: (status: SyncStatus) => void
  setLastSyncTime: (time: string) => void
  clearSyncError: () => void
}

function toAuthUser(u: User): AuthUser {
  return {
    uid: u.uid,
    email: u.email || '',
    displayName: u.displayName || '',
    photoURL: u.photoURL || '',
  }
}

export const useAuthStore = create<AuthState>()((set) => ({
  user: null,
  memoUid: null,
  memoConnected: false,
  isLoading: false,
  isSigningIn: false,
  syncStatus: 'idle',
  lastSyncTime: null,
  lastSyncError: null,
  error: null,

  initialize: () => {
    // Lazy load firebase auth
    getAuthInstance().then(async (auth) => {
      const { getRedirectResult, onAuthStateChanged } = await import('firebase/auth')

      // Handle redirect result from signInWithRedirect
      getRedirectResult(auth)
        .then((result) => {
          if (result?.user) {
            set({ isSigningIn: false })
          }
        })
        .catch((err: any) => {
          console.error('Redirect result error:', err)
          // #26: 에러 유형별 메시지 구분
          const code = err?.code || ''
          let message = 'Google 로그인에 실패했습니다. 다시 시도해주세요.'
          if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
            message = '로그인이 취소되었습니다.'
          } else if (code === 'auth/popup-blocked') {
            message = '팝업이 차단되었습니다. 브라우저 설정을 확인하세요.'
          } else if (code === 'auth/network-request-failed') {
            message = '네트워크 연결을 확인하세요.'
          }
          set({ isSigningIn: false, error: message })
        })

      onAuthStateChanged(auth, async (firebaseUser) => {
        if (firebaseUser) {
          set({ user: toAuthUser(firebaseUser), isLoading: false, isSigningIn: false, syncStatus: 'syncing', lastSyncError: null })
          // 개인 동기화 + 공유 그룹 초기화를 병렬 실행 (로딩 속도 개선)
          const syncPromise = initSync(firebaseUser.uid)
            .then(() => set({ syncStatus: 'synced', lastSyncTime: new Date().toISOString(), lastSyncError: null }))
            .catch((err) => {
              console.error('Sync init failed:', err)
              const msg = err instanceof Error ? err.message : '클라우드 동기화에 실패했습니다.'
              set({ syncStatus: 'error', lastSyncError: msg })
            })

          const groupPromise = import('./sharedGroupStore')
            .then(({ useSharedGroupStore }) => useSharedGroupStore.getState().initialize())
            .catch((err) => console.error('SharedGroup init failed:', err))

          await Promise.all([syncPromise, groupPromise])
          // Memo Firebase 연동 — currentUser 또는 onAuthStateChanged로 복원
          try {
            const { getMemoAuthInstance } = await import('@/lib/memoFirebase')
            const memoAuth = await getMemoAuthInstance()
            if (memoAuth) {
              const initMemo = async (uid: string) => {
                set({ memoUid: uid, memoConnected: true })
                try {
                  const { useMemoLinkStore } = await import('./memoLinkStore')
                  await useMemoLinkStore.getState().initialize(uid)
                } catch { /* ignore */ }
              }
              if (memoAuth.currentUser) {
                initMemo(memoAuth.currentUser.uid)
              } else {
                // 앱 재시작 시 Memo Auth 자동 복원 대기
                const { onAuthStateChanged: onMemoAuth } = await import('firebase/auth')
                let memoUnsub: (() => void) | null = null
                memoUnsub = onMemoAuth(memoAuth, (memoUser) => {
                  if (memoUser) { initMemo(memoUser.uid) }
                  if (memoUnsub) { memoUnsub(); memoUnsub = null }
                })
                // 5초 후에도 복원 안 되면 리스너 해제
                setTimeout(() => { if (memoUnsub) { memoUnsub(); memoUnsub = null } }, 5000)
              }
            }
          } catch (err) {
            console.warn('[Memo] onAuthStateChanged 연동 실패:', err instanceof Error ? err.message : err)
          }
          // FCM: 로그인 시 토큰 갱신 또는 자동 활성화 제안
          try {
            const { useSettingsStore } = await import('./settingsStore')
            const settings = useSettingsStore.getState().settings
            if (settings.fcmPushEnabled) {
              // 이미 활성화됨 → 토큰 갱신
              const { refreshFCMToken } = await import('../services/fcmService')
              refreshFCMToken(firebaseUser.uid).catch(console.error)
            } else if (!settings.fcmPermissionAsked) {
              // 아직 권한 요청한 적 없음 → 자동 요청
              setTimeout(async () => {
                try {
                  const { requestFCMPermission } = await import('../services/fcmService')
                  const token = await requestFCMPermission(firebaseUser.uid)
                  if (token) {
                    useSettingsStore.getState().setFCMPush(true)
                  }
                  useSettingsStore.getState().setFCMPermissionAsked(true)
                } catch { /* FCM not critical */ }
              }, 3000) // 3초 후 요청 (UX: 로그인 직후 즉시 팝업 방지)
            }
          } catch { /* FCM not critical */ }
        } else {
          // FCM 토큰 삭제 (로그아웃 시)
          const previousUser = useAuthStore.getState().user
          if (previousUser) {
            try {
              const { useSettingsStore } = await import('./settingsStore')
              if (useSettingsStore.getState().settings.fcmPushEnabled) {
                const { unregisterFCMToken } = await import('../services/fcmService')
                unregisterFCMToken(previousUser.uid).catch(console.error)
              }
            } catch { /* FCM not critical */ }
          }
          stopSync()
          // 공유 그룹 정리
          try {
            const { useSharedGroupStore } = await import('./sharedGroupStore')
            useSharedGroupStore.getState().cleanup()
          } catch { /* ignore */ }
          // Memo 상태 정리
          try {
            const { useMemoLinkStore } = await import('./memoLinkStore')
            useMemoLinkStore.getState().cleanup()
          } catch { /* ignore */ }
          set({ user: null, memoUid: null, memoConnected: false, isLoading: false, syncStatus: 'idle', lastSyncTime: null, lastSyncError: null })
        }
      })
    })
  },

  login: async () => {
    set({ error: null, isSigningIn: true })
    try {
      const auth = await getAuthInstance()
      const { signInWithPopup, signInWithRedirect, GoogleAuthProvider } = await import('firebase/auth')
      const provider = new GoogleAuthProvider()

      try {
        // 모든 디바이스에서 popup 우선 시도 (COOP: same-origin-allow-popups 설정됨)
        // 팝업 차단/COOP 실패 시 catch에서 redirect 폴백
        await signInWithPopup(auth, provider)
        // Memo Firebase 인증
        try {
          const { getMemoAuthInstance } = await import('@/lib/memoFirebase')
          const memoAuth = await getMemoAuthInstance()

          if (!memoAuth) {
            console.error('[Memo] getMemoAuthInstance null — 환경변수 확인 필요')
          } else {
            let memoUser: import('firebase/auth').User | null = null

            // 잘못된 세션 정리 후 Cloud Function으로 정확한 Memo UID 인증
            if (memoAuth.currentUser) {
              const { signOut: memoSignOut } = await import('firebase/auth')
              await memoSignOut(memoAuth)
              console.info('[Memo] 기존 세션 정리 완료, Cloud Function으로 재인증')
            }

            try {
                const { getFunctions, httpsCallable } = await import('firebase/functions')
                const functions = getFunctions(undefined, 'asia-northeast3')
                const createMemoToken = httpsCallable(functions, 'createMemoToken')
                const result = await createMemoToken({})
                const { customToken } = result.data as { customToken: string }

                const { signInWithCustomToken } = await import('firebase/auth')
                const memoResult = await signInWithCustomToken(memoAuth, customToken)
                memoUser = memoResult.user
                console.info('[Memo] 커스텀 토큰 인증 성공, uid:', memoUser.uid, 'email:', memoUser.email)
              } catch (e) {
                console.warn('[Memo] 커스텀 토큰 인증 실패:', (e as Error).message)
              }

            if (memoUser) {
              set({ memoUid: memoUser.uid, memoConnected: true })
              const { useMemoLinkStore } = await import('./memoLinkStore')
              await useMemoLinkStore.getState().initialize(memoUser.uid)
            } else {
              console.error('[Memo] 모든 인증 방식 실패 — Memo 연동 불가')
            }
          }
        } catch (err) {
          const errMsg = err instanceof Error ? err.message : String(err)
          const errCode = (err as { code?: string })?.code
          console.error('[Memo] 연동 실패:', errCode, errMsg)
        }
      } catch (err: unknown) {
        const firebaseErr = err as { code?: string; message?: string }
        const isPopupError =
          firebaseErr.code === 'auth/popup-blocked' ||
          firebaseErr.code === 'auth/popup-closed-by-user' ||
          firebaseErr.code === 'auth/cancelled-popup-request' ||
          firebaseErr.code === 'auth/internal-error' ||
          // COOP 헤더로 팝업 통신 차단 시 Firebase 내부 에러 발생
          (firebaseErr.message?.includes('INTERNAL ASSERTION FAILED') ?? false) ||
          (firebaseErr.message?.includes('Cross-Origin-Opener-Policy') ?? false)

        if (isPopupError) {
          try {
            await signInWithRedirect(auth, provider)
            return
          } catch (redirectErr) {
            const message = redirectErr instanceof Error ? redirectErr.message : '로그인에 실패했습니다.'
            set({ error: message, isSigningIn: false })
          }
        } else {
          const message = err instanceof Error ? err.message : '로그인에 실패했습니다.'
          set({ error: message, isSigningIn: false })
        }
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : '로그인에 실패했습니다.'
      set({ error: message, isSigningIn: false })
    }
  },

  logout: async () => {
    try {
      // #18: 스토어를 비우기 전에 uid를 캡처하고 FCM 토큰을 명시적으로 해제.
      // user:null을 먼저 set하면 onAuthStateChanged(null)의 cleanup이 previousUser를
      // 읽지 못해 토큰이 이 기기에 남는다 → 로그아웃 후에도 푸시 수신(프라이버시 누출).
      // signOut 이전에 수행해야 SW 등록/토큰이 아직 유효하다.
      const previousUid = useAuthStore.getState().user?.uid
      if (previousUid) {
        try {
          const { useSettingsStore } = await import('./settingsStore')
          if (useSettingsStore.getState().settings.fcmPushEnabled) {
            const { unregisterFCMToken } = await import('../services/fcmService')
            await unregisterFCMToken(previousUid) // Firestore 토큰 doc 삭제 + deleteToken(messaging)
          }
        } catch { /* FCM not critical */ }
      }

      // C3: 동기화 중단 → 로컬 데이터 정리 → Firebase 로그아웃 순서
      stopSync()

      // #24: sync queue 비우기 — stale cloud delete/push가 다른 세션/계정으로 재생되지 않도록
      clearSyncQueue()

      // Memo: 세션은 유지 (signOut하지 않음 — 재로그인 시 자동 복원용)
      // Memo store 정리
      try {
        const { useMemoLinkStore } = await import('./memoLinkStore')
        useMemoLinkStore.getState().cleanup()
      } catch { /* ignore */ }

      // 1) 로컬 데이터 먼저 정리 (signOut 실패해도 이전 사용자 데이터 노출 방지)
      await clearAllData()

      // Reset Zustand stores to empty state
      const { useTaskStore } = await import('./taskStore')
      const { useCategoryStore } = await import('./categoryStore')
      const { useGroupStore } = await import('./groupStore')

      useTaskStore.setState({ tasks: [], activeTaskId: null, isLoading: false, error: null })
      useCategoryStore.setState({ categories: [], isLoading: false })
      useGroupStore.setState({ groups: [], isLoading: false })

      // 공유 그룹 정리
      const { useSharedGroupStore } = await import('./sharedGroupStore')
      useSharedGroupStore.getState().cleanup()

      set({ user: null, memoUid: null, memoConnected: false, syncStatus: 'idle', lastSyncTime: null, lastSyncError: null, error: null })

      // 2) Firebase 로그아웃 (best-effort — 실패해도 로컬은 이미 정리됨)
      try {
        const auth = await getAuthInstance()
        const { signOut } = await import('firebase/auth')
        await signOut(auth)
      } catch {
        // Firebase 로그아웃 실패는 무시 — 로컬 데이터는 이미 삭제됨
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : '로그아웃에 실패했습니다.'
      set({ error: message })
    }
  },

  // 수동 재동기화 — 동기화 실패 배너의 '다시 동기화' 버튼에서 사용.
  // initSync는 currentUserId가 같으면 조기 반환하므로 stopSync()로 먼저 초기화한 뒤 재실행한다.
  retrySync: async () => {
    const currentUser = useAuthStore.getState().user
    if (!currentUser) return
    set({ syncStatus: 'syncing', lastSyncError: null })
    try {
      stopSync()
      await initSync(currentUser.uid)
      set({ syncStatus: 'synced', lastSyncTime: new Date().toISOString(), lastSyncError: null })
      // 공유 그룹도 함께 재초기화 (best-effort)
      try {
        const { useSharedGroupStore } = await import('./sharedGroupStore')
        await useSharedGroupStore.getState().initialize()
      } catch { /* 공유 그룹 재초기화 실패는 무시 — 개인 동기화는 성공 */ }
    } catch (err) {
      console.error('Manual resync failed:', err)
      const msg = err instanceof Error ? err.message : '클라우드 동기화에 실패했습니다.'
      set({ syncStatus: 'error', lastSyncError: msg })
    }
  },

  setSyncStatus: (status) => set({ syncStatus: status }),
  setLastSyncTime: (time) => set({ lastSyncTime: time }),
  clearSyncError: () => set({ lastSyncError: null }),
}))
